// framework
import angularRedux from '@buildium/angular-redux';

// third-party
import 'jquery-migrate';
import uiRouter from '@uirouter/angularjs';
import '@uirouter/angularjs/lib/legacy/stateEvents.js';

// private package
import { BrowserModule } from '@buildium/angularjs-core/dist/browser';
import { LoadingSpinnerModule } from '@buildium/angularjs-core/dist/components/loading-spinner';
import { SiteChromeModule } from '@buildium/angularjs-core/dist/site-chrome';
import { FormValidationAndHelpersModule } from '@buildium/angularjs-core/dist/forms';
import { FormElementModule } from '@buildium/angularjs-core/dist/forms/components/form-element';
import { ModalModule } from '@buildium/angularjs-core/dist/components/modal';
import { NotificationsModule } from '@buildium/angularjs-core/dist/notifications';
import { ResourceStringsModule } from '@buildium/angularjs-core/dist/components/resource-strings';
import { FormattersModule } from '@buildium/angularjs-core/dist/formatters';
import { ConstantsModule } from '@buildium/angularjs-core/dist/constants';
import { NavigateModule } from '@buildium/angularjs-core/dist/navigate';
import { StorageModule } from '@buildium/angularjs-core/dist/storage';
import { UtilsModule } from '@buildium/angularjs-core/dist/utilities';
import { FormActionsModule } from '@buildium/angularjs-core/dist/forms/validation/framework/form-actions';
import { SubmitModule } from '@buildium/angularjs-core/dist/forms/components/submit';
import { PopoverModule } from '@buildium/angularjs-core/dist/components/popover';

// manager-base
import { ManagerAppBaseRoutes } from '../../routes';
import { BootstrapUnauthenticatedManagerRun } from '../../run/bootstrap-unauthenticated-manager.run';
import { ManagerGlobalStateModule } from '../../../global-state/global-state.module';

// config
import { AppAnalyticsRun } from '../../run/analytics';
import { StateChangeEventsRun } from '../../run/state-change-events';
import { ErrorOverrideService } from '../../config/error-override';

// feature
import { ManagerResourceStringsModule } from '../../../resource-strings/resource-strings.module';
import { LoggerModule } from '../../../logger-config/logger-config.module';
import { RecaptchaSettingsModule } from '../../../recaptcha-settings/recaptcha-settings.module.ts';
import { MobileBannerModule } from '../../../mobile-banner/mobile-banner.module.ts';
import { GlobalizeLoaderModule } from '../../../globalize-loader/globalize-loader.module.ts';
import { ManagerApiModule } from '../../../api/manager-api.module';
import { EsignatureRequestSignModalModule } from '../../../leases/features/Esignatures/components/esignature-request-sign-modal/esignature-request-sign-modal.module';
import { ManagerSettingsModule } from '../../../settings/settings.module';
import { MailgunValidationDirectiveModule } from '../../../directives/rule-directives/mailgun/mailgun.module';
import { UnauthenticatedAppcuesModule } from '../../../unathenticated-appcues/unauthenticated-appcues.module';

// feature services
import { PublicResetPasswordApi } from '../../services/public-reset-password-service';
import { PublicForgotPasswordApi } from '../../services/public-forgot-password-service';
import { PublicPageLogApi } from '../../services/public-log-service';
import { PublicSetPasswordApi } from '../../services/public-set-password-service';
import { SetupTwoFactorAuthenticationApi } from '../../services/set-up-two-factor-authentication';
import { PublicTwoFactorAuthenticationApi } from '../../services/public-two-factor-authentication-service';
import { PublicAuthenticationApi } from '../../services/public-authentication-service';
import { PublicFeatureFlagsService } from '../../services/public-feature-flags';
import { AnalyticsService } from '../../analytics/analytics';
import { RedirectToLoginService } from '../../config/redirect-to-login';
import { StateAccessControlService } from '../../services/state-access-control';
import { ImagePathModule } from '../../image-path.module';
import { PathsConstantsModule } from '../../environment/paths/paths-constants.module';

export const ManagerPublicAuthenticationBaseModule = angularRedux.createModule('buildium.manager-public-authentication', [
    uiRouter,
    'ui.router.state.events',
    ConstantsModule.name,
    FormattersModule.name,
    LoggerModule.name,
    NavigateModule.name,
    StorageModule.name,
    UtilsModule.name,
    FormActionsModule.name,
    SubmitModule.name,
    PopoverModule.name,
    'buildium.cache', // this module writes information from the page to the $http cache
    ManagerGlobalStateModule.name,
    BrowserModule.name,
    ResourceStringsModule.name,
    LoadingSpinnerModule.name,
    NotificationsModule,
    SiteChromeModule.name,
    FormValidationAndHelpersModule.name,
    MailgunValidationDirectiveModule.name,
    FormElementModule.name,
    ModalModule.name,
    ManagerApiModule.name,
    ManagerSettingsModule.name,
    ManagerResourceStringsModule,
    ImagePathModule,
    MobileBannerModule.name,
    GlobalizeLoaderModule.name,
    RecaptchaSettingsModule.name,
    PathsConstantsModule.name,
    EsignatureRequestSignModalModule.name,
    UnauthenticatedAppcuesModule.name
])
    .config(ManagerAppBaseRoutes)
    .constant('IS_PUBLIC_MODULE', true)
    .service('SetupTwoFactorAuthenticationApi', SetupTwoFactorAuthenticationApi)
    .service('PublicResetPasswordApi', PublicResetPasswordApi)
    .service('PublicForgotPasswordApi', PublicForgotPasswordApi)
    .service('PublicSetPasswordApi', PublicSetPasswordApi)
    .service('PublicTwoFactorAuthenticationApi', PublicTwoFactorAuthenticationApi)
    .service('PublicAuthenticationApi', PublicAuthenticationApi)
    .service('PublicPageLogApi', PublicPageLogApi)
    .service('PublicFeatureFlagsService', PublicFeatureFlagsService)
    .run(StateChangeEventsRun)
    .run(AppAnalyticsRun)
    .run(BootstrapUnauthenticatedManagerRun)
    .service('Analytics', AnalyticsService)
    .service('RedirectToLogin', RedirectToLoginService)
    .factory('$exceptionHandler', ErrorOverrideService)
    .service('StateAccessControl', StateAccessControlService)

    .config(['$locationProvider', '$urlMatcherFactoryProvider', '$urlRouterProvider', function setAsyncRoutes($locationProvider, $urlMatcherFactoryProvider, $urlRouterProvider) {
        $locationProvider.html5Mode(true);
        $urlMatcherFactoryProvider.caseInsensitive(true);
        $urlMatcherFactoryProvider.strictMode(false);

        $urlRouterProvider.otherwise(function handleCaseSensitivity(injector, location) {
            const path = location.path(), normalized = path.toLowerCase();
            if (path !== normalized) {
                location.replace().path(normalized);
            }
        });
    }]);
